'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Loader2, Save, GripVertical } from 'lucide-react';

interface Service {
  id: string;
  name: string;
  description: string;
  priceFrom: string;
}

const initialServices: Service[] = [
  { id: 'svc-mgmt-001', name: 'Home Extensions', description: 'Single and double-storey extensions designed and built to the highest standard.', priceFrom: '25,000' },
  { id: 'svc-mgmt-002', name: 'Loft Conversions', description: 'Maximise your living space with a bespoke loft conversion.', priceFrom: '18,000' },
  { id: 'svc-mgmt-003', name: 'New Build Projects', description: 'Full new build construction for residential and light commercial clients.', priceFrom: '150,000' },
  { id: 'svc-mgmt-004', name: 'Kitchen & Bathroom Renovations', description: 'Full strip-out and refit service including plumbing, electrics, tiling and joinery.', priceFrom: '8,000' },
  { id: 'svc-mgmt-005', name: 'Garage Conversions', description: 'Convert your garage into a usable living space, home office or gym.', priceFrom: '12,000' },
];

export default function ServicesTab() {
  const [services, setServices] = useState<Service[]>(initialServices);
  const [isLoading, setIsLoading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const addService = () => {
    const newId = `svc-mgmt-new-${Date.now()}`;
    setServices((prev) => [
      ...prev,
      { id: newId, name: '', description: '', priceFrom: '' },
    ]);
  };

  const updateService = (id: string, field: keyof Service, value: string) => {
    setServices((prev) => prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)));
  };

  const deleteService = (id: string) => {
    setDeletingId(id);
    setTimeout(() => {
      setServices((prev) => prev.filter((s) => s.id !== id));
      setDeletingId(null);
      toast.success('Service removed');
    }, 300);
  };

  const handleSave = async () => {
    setIsLoading(true);
    // Backend integration point: PUT /api/businesses/{id}/services
    await new Promise((r) => setTimeout(r, 1000));
    toast.success('Services saved successfully');
    setIsLoading(false);
  };

  return (
    <div>
      <div className="card-surface p-6 mb-5">
        <div className="flex items-center justify-between mb-5 pb-3 border-b border-border">
          <div>
            <h3 className="font-bold text-base text-foreground">Services &amp; Products</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Add the services your business offers. These are displayed on your listing page.
            </p>
          </div>
          <button
            type="button"
            onClick={addService}
            className="btn-primary text-sm py-2"
          >
            <Plus size={14} />
            Add Service
          </button>
        </div>

        <div className="space-y-4">
          {services.map((svc) => (
            <div
              key={svc.id}
              className={`border border-border rounded p-4 transition-all duration-300 ${
                deletingId === svc.id ? 'opacity-0 max-h-0 overflow-hidden p-0 m-0 border-0' : 'opacity-100'
              }`}
            >
              <div className="flex items-start gap-3">
                <div className="text-muted-foreground/40 mt-2 cursor-grab shrink-0">
                  <GripVertical size={16} />
                </div>
                <div className="flex-1 space-y-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-semibold text-foreground mb-1">
                        Service name <span className="text-negative">*</span>
                      </label>
                      <input
                        type="text"
                        value={svc.name}
                        onChange={(e) => updateService(svc.id, 'name', e.target.value)}
                        className="input-field text-sm"
                        placeholder="e.g. Home Extensions"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-foreground mb-1">
                        Price from (£)
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                        <input
                          type="text"
                          value={svc.priceFrom}
                          onChange={(e) => updateService(svc.id, 'priceFrom', e.target.value)}
                          className="input-field text-sm pl-6 font-mono"
                          placeholder="Leave blank if variable"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-foreground mb-1">
                      Description
                    </label>
                    <textarea
                      rows={2}
                      value={svc.description}
                      onChange={(e) => updateService(svc.id, 'description', e.target.value)}
                      className="input-field text-sm resize-none"
                      placeholder="Brief description of this service..."
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => deleteService(svc.id)}
                  className="text-muted-foreground hover:text-negative transition-colors p-1.5 rounded hover:bg-negative/10 shrink-0 mt-1"
                  aria-label={`Remove ${svc.name || 'service'}`}
                >
                  <Trash2 size={15} />
                </button>
              </div>
            </div>
          ))}

          {services.length === 0 && (
            <div className="text-center py-10 text-muted-foreground">
              <Package size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-sm font-semibold">No services added yet</p>
              <p className="text-xs mt-1">Add the services you offer to help customers understand what you do.</p>
              <button type="button" onClick={addService} className="btn-primary mt-4 text-sm">
                <Plus size={14} />
                Add Your First Service
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 -mx-6 flex justify-end gap-3">
        <button type="button" className="btn-secondary text-sm">Cancel</button>
        <button
          type="button"
          onClick={handleSave}
          disabled={isLoading}
          className="btn-primary text-sm"
          style={{ minWidth: 160 }}
        >
          {isLoading ? <><Loader2 size={14} className="animate-spin" />Saving...</> : <><Save size={14} />Save Services</>}
        </button>
      </div>
    </div>
  );
}

function Package({ size, className }: { size: number; className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="m7.5 4.27 9 5.15" />
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
      <path d="m3.3 7 8.7 5 8.7-5" />
      <path d="M12 22V12" />
    </svg>
  );
}